import {
  faFlag,
  faImagePortrait,
  faTrash,
  faUpload,
} from "@fortawesome/pro-regular-svg-icons";
import { MenuProps } from "@mui/material";
import pluralize from "pluralize";
import { useBoolean } from "usehooks-ts";

import { ReportFileMenu } from "@/components/selection-sidebar/ReportFileMenu";
import {
  ConfirmDialog,
  Divider,
  Icon,
  Menu,
  MenuItem,
} from "@/components/ui-v2";
import { useAvailableActionIds } from "@/features/files/hooks/useAvailableActionIds";
import { useSetAsStyleCover } from "@/hooks/fileImageActions/useSetAsStyleCover";
import { useDeleteObjects } from "@/hooks/useDeleteObjects";
import { useUpdateFiles } from "@/hooks/useUpdateFiles";

import { SelectedObject, SidebarContext } from "./types";

interface SelectionSidebarHeaderMenuProps {
  context: SidebarContext;
  objects: SelectedObject[];
  onClose: () => void;
  anchorEl?: HTMLElement;
  onDeleted?: () => void;
}

export const SelectionSidebarHeaderMenu = (
  props: SelectionSidebarHeaderMenuProps,
) => {
  const { context, objects, onClose, onDeleted, anchorEl } = props;

  const availableActionIds = useAvailableActionIds({
    objects,
    pageContext: context,
  });

  const { updateFiles, result: updateFilesResult } = useUpdateFiles();
  const { deleteObjects, result: deleteFilesResult } = useDeleteObjects();
  const { setAsStyleCover } = useSetAsStyleCover();

  const {
    value: isStyleCoverImageConfirmDialogOpen,
    setTrue: openStyleCoverImageConfirmDialog,
    setFalse: closeStyleCoverImageConfirmDialog,
  } = useBoolean();

  const objectsIds = objects.map((obj) => obj.id);
  const allObjectsAreFiles = objects.every((obj) => obj.__typename === "File");
  const isAllObjectsPosted = objects.every(
    (obj) => obj.__typename === "File" && obj.isPosted,
  );

  const handlePostFiles = async () => {
    if (!allObjectsAreFiles) {
      return;
    }

    const isPosting = !isAllObjectsPosted;

    await updateFiles(
      objectsIds,
      { isPosted: isPosting },
      `${objectsIds.length > 1 ? "All files" : "File"} successfully ${isPosting ? "posted to" : "unposted from"} the model gallery.`,
    );

    if (!isPosting && context === "style-gallery") {
      // If unposting, we need to reload the objects this will trigger it
      onDeleted?.();
    }
  };

  const getStyleName = () => {
    if (!allObjectsAreFiles) {
      return "model";
    }

    const file = objects[0];

    if (file && file.styles && file.styles.length) {
      return file.styles[0].name;
    }

    return "model";
  };

  const handleConfirmSetAsCoverImage = () => {
    if (!allObjectsAreFiles) {
      return;
    }

    const file = objects[0];

    if (file && file.styles && file.styles.length > 0) {
      setAsStyleCover(file.styles[0], file);
    }
  };

  return (
    <>
      <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={onClose}>
        {availableActionIds.has("SET_AS_COVER_IMAGE") && (
          <MenuItem
            icon={<Icon icon={faImagePortrait} />}
            onClick={() => {
              openStyleCoverImageConfirmDialog();
              onClose();
            }}
          >
            Set as Model Cover Image
          </MenuItem>
        )}
        {availableActionIds.has("POST") && (
          <MenuItem
            loading={updateFilesResult.fetching}
            icon={<Icon icon={faUpload} />}
            onClick={handlePostFiles}
          >
            {isAllObjectsPosted
              ? "Remove from Model Gallery"
              : "Post to Model Gallery"}
          </MenuItem>
        )}
        {(availableActionIds.has("SET_AS_COVER_IMAGE") ||
          availableActionIds.has("POST")) && <Divider />}
        {availableActionIds.has("REPORT") && (
          <MenuItem
            hasSubMenu
            label={`Report Image${objects.length > 1 ? "s" : ""}`}
            icon={<Icon icon={faFlag} />}
            renderSubMenu={(menuProps: MenuProps) => (
              <ReportFileMenu {...menuProps} fileIds={objectsIds} />
            )}
          >
            Report {pluralize("Item", objects.length)}
          </MenuItem>
        )}
        {availableActionIds.has("DELETE") && (
          <MenuItem
            loading={deleteFilesResult.fetching}
            icon={<Icon icon={faTrash} />}
            onClick={() => deleteObjects(objectsIds, onDeleted)}
          >
            Delete {pluralize("Item", objects.length)}
          </MenuItem>
        )}
      </Menu>
      <ConfirmDialog
        open={isStyleCoverImageConfirmDialogOpen}
        title="Set as Model Cover Image"
        description={`Are you sure you want to set this image as the ${getStyleName()} cover image?`}
        onConfirm={() => {
          closeStyleCoverImageConfirmDialog();
          handleConfirmSetAsCoverImage();
        }}
        onClose={() => {
          closeStyleCoverImageConfirmDialog();
        }}
      />
    </>
  );
};
